import { Component, inject, input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { CartActions } from '@next/core-lib/cart';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';

import { Subject, takeUntil } from 'rxjs';

import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Insights } from '../../shared/constants';
import {
  EmergenceDateNotAdjustedNotificationDto,
  HeatstressDetectedForCustomerNotificationDto,
  MissingCropFieldDataNotificationDto,
  MissingNematodeAnalysisNotificationDto,
  MissingPcnAnalysisNotificationDto,
  NotificationCategory,
  NotificationDto,
  NotificationType,
  PlantingDateNotAdjustedNotificationDto,
  QuestionnaireNotCompletedNotificationDto,
  SupplementaryFertilizerAdvisedNotificationDto,
  TgaSteps,
} from '../../shared/models/models';
import { closeNotification } from '../../store/tga.actions';
import { getTgaCropYear } from '../../store/tga.selectors';
// eslint-disable-next-line max-len
import { HeatstressDetectedForCustomerBodyComponent } from '../notification-bodies/heatstress-detected-for-customer-body/heatstress-detected-for-customer-body.component';
import { MissingCropFieldDataBodyComponent } from '../notification-bodies/missing-cropfield-data-body.component/missing-cropfield-data-body.component';
import { MissingOrIncorrectBodyComponent } from '../notification-bodies/missing-or-incorrect-body.component/missing-or-incorrect-body.component';
// eslint-disable-next-line max-len
import { QuestionnaireNotCompletedBodyComponent } from '../notification-bodies/questionnaire-not-completed-body.component/questionnaire-not-completed-body.component';
// eslint-disable-next-line max-len
import { SupplementaryFertilizerAdvisedBodyComponent } from '../notification-bodies/supplementary-fertilizer-advised-body.component/supplementary-fertilizer-advised-body.component';

@Component({
  selector: 'app-notification-card',
  templateUrl: './notification-card.component.html',
  styleUrls: ['./notification-card.component.scss'],
  imports: [
    MatIcon,
    QuestionnaireNotCompletedBodyComponent,
    MissingCropFieldDataBodyComponent,
    SupplementaryFertilizerAdvisedBodyComponent,
    HeatstressDetectedForCustomerBodyComponent,
    MissingOrIncorrectBodyComponent,
    MatButton,
    TranslatePipe,
  ],
})
export class NotificationCardComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly localeService = inject(LocaleService);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);

  notification = input.required<NotificationDto>();
  private readonly destroy$ = new Subject<void>();
  private readonly cropYear$ = this.store.select(getTgaCropYear);
  private readonly customer$ = this.store.pipe(select(UserSelectors.selectCustomer), takeUntil(this.destroy$));
  private readonly location$ = this.store.pipe(select(UserSelectors.selectLocation), takeUntil(this.destroy$));
  customerId = 0;
  locationId = 0;
  locale: string;

  readonly NotificationType = NotificationType;

  constructor() {
    this.locale = this.localeService.getLocale();
    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale = this.localeService.getLocale();
    });
    this.customer$.subscribe((customer) => {
      this.customerId = customer.id;
    });
    this.location$.subscribe((location) => {
      this.locationId = location.id;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClose() {
    const notification = this.notification();
    const notificationId = notification.id;
    if (notificationId && notification.canBeClosed) {
      this.store.dispatch(closeNotification({ notificationId }));
    }
  }

  onAction(): void {
    const notif = this.notification();
    if (notif.category == NotificationCategory.Warning) {
      this.ai.trackEvent(Insights.clickedWarning(notif.type), {});
    } else {
      this.ai.trackEvent(Insights.clickedNotification(notif.type), {});
    }
    this.cropYear$.subscribe((cropYear) => {
      switch (notif.type) {
        case NotificationType.QuestionnaireNotCompleted: {
          // Navigate to generic questionnaire based on which step is missing
          const questionnaireNotif = this.asQuestionnaireNotCompleted(notif);
          let step = TgaSteps.General; // Default to general if missingGeneral is true

          // Determine which step to navigate to based on what's missing
          if (questionnaireNotif.missingGeneral) {
            step = TgaSteps.General;
          } else if (questionnaireNotif.missingSoil) {
            step = TgaSteps.Soil;
          } else if (questionnaireNotif.missingPlanting) {
            step = TgaSteps.Planting;
          }

          this.router.navigate(['/tga/cropfields', step, 'questionnaire', 'generic', cropYear]);
          break;
        }

        case NotificationType.MissingCropFieldData: {
          const cropFieldNotif = this.asMissingCropFieldData(notif);
          // If there are crop fields, navigate to the first one's specific questionnaire
          if (cropFieldNotif.cropFields && cropFieldNotif.cropFields.length > 0) {
            const cropFieldId = cropFieldNotif.cropFields[0].cropFieldId;
            this.router.navigate(['/tga/cropfields', TgaSteps.General, 'questionnaire', 'specific', cropYear, cropFieldId]);
          } else {
            // Fallback to selection page if no crop field IDs
            this.router.navigate(['/tga/cropfields']);
          }
          break;
        }

        case NotificationType.MissingNematodeAnalysis:
        case NotificationType.MissingPcnAnalysis: {
          const missingNotif = this.asMissingOrIncorrectData(notif);
          // Navigate to first crop field's questionnaire (single or multiple)
          if (missingNotif.cropFields && missingNotif.cropFields.length > 0) {
            const cropFieldId = missingNotif.cropFields[0].cropFieldId;
            this.router.navigate(['/tga/cropfields', TgaSteps.Soil, 'questionnaire', 'specific', cropYear, cropFieldId]);
          }
          break;
        }
        case NotificationType.PlantingDateNotAdjusted:
        case NotificationType.EmergenceDateNotAdjusted: {
          const dateNotAdjustedNotif = this.asMissingOrIncorrectData(notif);
          if (dateNotAdjustedNotif.cropFields.length > 0) {
            const cropFieldId = dateNotAdjustedNotif.cropFields[0].cropFieldId;
            this.router.navigate(['/blightV2'], { queryParams: { cropFieldId, 'tab': 'insights' } });
          }
          break;
        }
        case NotificationType.SupplementaryFertilizerAdvised: {
          const supplementaryNotif = this.asSupplementaryFertilizerAdvised(notif);
          this.router.navigate(['/bem', supplementaryNotif.cropFieldId]);
          break;
        }
        case NotificationType.HeatstressDetectedForCustomer: {
          const returnUrl = globalThis.location.href;
          this.store.dispatch(
            CartActions.openCartUrl({
              customerId: this.customerId,
              locationId: this.locationId,
              returnUrl,
            }),
          );
          break;
        }
      }
    }).unsubscribe();
  }

  asQuestionnaireNotCompleted(notification: NotificationDto): QuestionnaireNotCompletedNotificationDto {
    return notification as QuestionnaireNotCompletedNotificationDto;
  }

  asMissingCropFieldData(notification: NotificationDto): MissingCropFieldDataNotificationDto {
    return notification as MissingCropFieldDataNotificationDto;
  }

  asSupplementaryFertilizerAdvised(notification: NotificationDto): SupplementaryFertilizerAdvisedNotificationDto {
    return notification as SupplementaryFertilizerAdvisedNotificationDto;
  }

  asHeatstressDetectedForCustomer(notification: NotificationDto): HeatstressDetectedForCustomerNotificationDto {
    return notification as HeatstressDetectedForCustomerNotificationDto;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString(this.locale, { day: 'numeric', month: 'long', year: 'numeric' });
  }

  asMissingOrIncorrectData(
    notification: NotificationDto,
  ): MissingNematodeAnalysisNotificationDto |
    MissingPcnAnalysisNotificationDto |
    PlantingDateNotAdjustedNotificationDto |
    EmergenceDateNotAdjustedNotificationDto {
    return notification as MissingNematodeAnalysisNotificationDto |
      MissingPcnAnalysisNotificationDto |
      PlantingDateNotAdjustedNotificationDto |
      EmergenceDateNotAdjustedNotificationDto;
  }



  getNotificationCropFieldsLength(notif: NotificationDto): number {
    if (notif.type === NotificationType.MissingCropFieldData) {
      return this.asMissingCropFieldData(notif).cropFields.length;
    }
    if (notif.type === NotificationType.MissingNematodeAnalysis ||
      notif.type === NotificationType.MissingPcnAnalysis ||
      notif.type === NotificationType.PlantingDateNotAdjusted ||
      notif.type === NotificationType.EmergenceDateNotAdjusted) {
      return this.asMissingOrIncorrectData(notif).cropFields.length;
    }

    if (notif.type === NotificationType.SupplementaryFertilizerAdvised) {
      return 1;
    }

    return 0;
  }

  getSingleCropFieldName(notif: NotificationDto): string {
    if (notif.type === NotificationType.MissingCropFieldData) {
      return this.asMissingCropFieldData(notif).cropFields[0].cropFieldName;
    }
    if (notif.type === NotificationType.MissingNematodeAnalysis ||
      notif.type === NotificationType.MissingPcnAnalysis ||
      notif.type === NotificationType.PlantingDateNotAdjusted ||
      notif.type === NotificationType.EmergenceDateNotAdjusted) {
      return this.asMissingOrIncorrectData(notif).cropFields[0].cropFieldName;
    }

    if (notif.type === NotificationType.SupplementaryFertilizerAdvised) {
      return this.asSupplementaryFertilizerAdvised(notif).cropFieldName;
    }

    return '';
  }
}
